// VYV Premium pricing — single source of truth for the client.
//
// IMPORTANT: the edge function `asaas-create-subscription` does NOT trust
// any value from the client. It has its own copy in
// `supabase/functions/_shared/pricingPlans.ts` and looks up `value` by plan
// id server-side. The client sends only the plan id ('monthly' | 'annual')
// and the method ('card' | 'pix'). Keep the numeric values here in sync
// with the server file.
//
// Locale-formatted labels live in i18n (`pricing.core.*`). This file owns
// the canonical numbers used to render checkout copy and to validate UI.

export type PlanId = "monthly" | "annual";
// Only the monthly plan is advertised for new purchases. Annual remains
// in the billing catalog for existing subscriptions and legacy clients.
export const PREMIUM_OFFER_PLAN_ID = "monthly" as const;

export type PaymentMethod = "card" | "pix";

export interface PlanDef {
  id: PlanId;
  value: number;
  cycle: "MONTHLY" | "YEARLY";
  days: number;
}

export const PREMIUM_PLANS: Record<PlanId, PlanDef> = {
  monthly: { id: "monthly", value: 29.90, cycle: "MONTHLY", days: 30 },
  annual: { id: "annual", value: 299.00, cycle: "YEARLY", days: 365 },
};

// Display helpers — UI shows labels from i18n; these helpers exist for the
// few places that need to render the raw BRL value (e.g. submit button).
const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

export function formatBRL(value: number): string {
  // SEM o espaço que o Intl insere entre "R$" e o número (U+00A0, espaço fino
  // não-quebrável). As 54 strings de preço do i18n escrevem "R$29,90" colado, e
  // o checkout renderiza pelo helper — com o espaço, a MESMA tela mostrava
  // "R$ 24,92" no subtítulo e "R$29,90" no card ao lado. Padronizar aqui mexe
  // em um lugar; padronizar no i18n mexeria em 54.
  // `\s` do JS já inclui o U+00A0 — não precisa (nem deve, por causa da regra
  // no-irregular-whitespace) do caractere literal aqui.
  return BRL.format(value).replace(/\s/g, "");
}

export function annualMonthlyEquivalent(): number {
  return Number((PREMIUM_PLANS.annual.value / 12).toFixed(2));
}

// ── Benefícios Premium — fonte única das DUAS vitrines (22/08) ──────────────
// Upgrade.tsx (tela de venda) e Pricing.tsx mantinham cada um a própria lista
// e elas divergiram SOZINHAS: o Upgrade ficou sem o f9 ("Mirror voice — 40
// min/month" — o maior delta free→premium medido: tts 3→40 min/mês) sem
// ninguém ter decidido isso. Mesmo movimento da fonte única de preços acima:
// as duas telas consomem ESTA lista, na ORDEM daqui, e
// src/test/pricingParity.test.ts pina as três regressões possíveis —
// chave fora dos 6 locales, chave aposentada (MORTAS) voltando, e qualquer
// uma das telas recriando lista local de pricing.core.f*.
// f10 (mensagens 30×) vem logo depois de "Everything in Free": é o maior
// multiplicador free→premium e a pergunta exata de quem bateu no teto de 40.
// f12 (Executor) entra no meio da lista; f11 (módulos) NÃO existe de
// propósito — Brain/Fitness/Nutrition foram LIBERADOS no Free (decisão de
// produto, ver PHASE1_LOCKED_MODULES vazio em Modules.tsx) e prometer módulo
// que não destrava numa tela de pagamento é o pior erro possível aqui. O
// buraco na numeração é deliberado, como o do f7.
export const PREMIUM_BENEFIT_KEYS = [
  "pricing.core.f1",
  "pricing.core.f10",
  "pricing.core.f2",
  "pricing.core.f4",
  "pricing.core.f12",
  "pricing.core.f5",
  "pricing.core.f6",
  "pricing.core.f8",
  "pricing.core.f9",
];

// ── Quotas EXIBIDAS na vitrine — pinadas contra a copy ──────────────────────
// A fonte OPERACIONAL destes números é o agent_config (chat.max_per_month
// premium=1200 · voice.tts_minutes_per_month premium=40) — o runtime lê de
// lá, nunca daqui. Estas constantes existem para UMA coisa: o pino em
// src/test/pricingParity.test.ts exige que as strings de f10 e f9 contenham
// estes números NOS 6 IDIOMAS. Mudou a quota no agent_config? Atualize aqui
// e a copy nos 6 locales — o teste vermelho é o lembrete; sem ele, número
// velho fica na tela em cinco idiomas até alguém reclamar (a classe de bug
// das 54 strings de preço, ver header do pricingParity).
export const PREMIUM_CHAT_PER_MONTH = 1200;
export const PREMIUM_VOICE_MINUTES = 40;
// LIVE has its own allowance, separate from Mirror TTS. Verified against
// agent_config voice.live_minutes_per_month on 2026-09-07 (Free 0, Premium 5).
export const PREMIUM_LIVE_MINUTES = 5;
